// ReportPage — convenience wrapper combining ReportShell + a route → page-meta lookup.
//
// Usage in page wrappers:
//
//   <ReportPage actions={...} filterBar={...} kpiStrip={...}>
//     <ExistingDashboardComponent />
//   </ReportPage>
//
// Title / subtitle / breadcrumb / accentColor are inferred from the current
// route in the central PAGE_META map below. Override any of them via props.
import React from 'react'
import { useLocation } from 'react-router-dom'
import ReportShell from './ReportShell'

const ORANGE = '#ff7900'
const PLUS   = '#00a651'
const TMOBILE= '#e20074'
const PLAY   = '#7b2cbf'

// Page metadata indexed by route path.
// Title and subtitle are short — they appear in the in-content header.
export const PAGE_META = {
  '/dashboard': {
    title: 'Dashboard główny',
    subtitle: 'Przegląd wyników KAM i operatorów telekomunikacyjnych — w czasie rzeczywistym.',
    breadcrumb: ['KAM-SFA', 'Dashboard'],
  },
  '/history': {
    title: 'Historia wyników',
    subtitle: 'Trendy sprzedaży i KPI w czasie — historia per operator i KAM.',
    breadcrumb: ['KAM-SFA', 'Historia'],
  },
  '/sales-map': {
    title: 'Mapa sprzedaży',
    subtitle: 'Geograficzna wizualizacja sprzedaży i pokrycia salonowego.',
    breadcrumb: ['KAM-SFA', 'Mapa sprzedaży'],
  },
  '/ai-analysis': {
    title: 'Analiza AI',
    subtitle: 'Rekomendacje, anomalie i insighty generowane przez modele językowe.',
    breadcrumb: ['KAM-SFA', 'Analiza AI'],
  },

  // Orange
  '/orange-salon-analysis': {
    title: 'Orange — Analiza sprzedaży per salon',
    subtitle: 'Sprzedaż, pokrycie i wydajność per pojedynczy salon Orange.',
    breadcrumb: ['KAM-SFA', 'Orange', 'Analiza sprzedaży'],
    accentColor: ORANGE,
  },
  '/orange-districts': {
    title: 'Orange — Analiza dystryktów',
    subtitle: 'Agregacja sprzedaży i KPI per dystrykt OPL.',
    breadcrumb: ['KAM-SFA', 'Orange', 'Analiza dystryktów'],
    accentColor: ORANGE,
  },
  '/orange-fte-analysis': {
    title: 'Orange — Sprzedaż vs FTE',
    subtitle: 'Wydajność sprzedażowa znormalizowana liczbą zatrudnionych (FTE) per region.',
    breadcrumb: ['KAM-SFA', 'Orange', 'Sprzedaż vs FTE'],
    accentColor: ORANGE,
  },
  '/orange-opaska-sales': {
    title: 'Orange — Opaska SOS · Sprzedaż',
    subtitle: 'Sprzedaż produktu Opaska SOS — agregaty i drill-down per KAM, salon, agent.',
    breadcrumb: ['KAM-SFA', 'Orange', 'Opaska SOS', 'Sprzedaż'],
    accentColor: ORANGE,
  },
  '/orange-vip-standard-zero': {
    title: 'Orange — Opaska SOS · VIP / Standard / Zero',
    subtitle: 'Klasyfikacja salonów Opaska SOS po wolumenie sprzedaży.',
    breadcrumb: ['KAM-SFA', 'Orange', 'Opaska SOS', 'VIP / Standard / Zero'],
    accentColor: ORANGE,
  },
  '/orange-locator-sales': {
    title: 'Orange — Lokalizator · Sprzedaż',
    subtitle: 'Sprzedaż produktu Lokalizator Orange — agregaty i drill-down.',
    breadcrumb: ['KAM-SFA', 'Orange', 'Lokalizator', 'Sprzedaż'],
    accentColor: ORANGE,
  },
  '/orange-locator-vip-standard-zero': {
    title: 'Orange — Lokalizator · VIP / Standard / Zero',
    subtitle: 'Klasyfikacja salonów Lokalizator po wolumenie sprzedaży.',
    breadcrumb: ['KAM-SFA', 'Orange', 'Lokalizator', 'VIP / Standard / Zero'],
    accentColor: ORANGE,
  },
  '/orange-locator-device-sales': {
    title: 'Orange — Lokalizator · Sprzedaż per urządzenie',
    subtitle: 'Sprzedaż urządzeń Lokalizator (lokalizator/smartwatch) per dzień i KAM.',
    breadcrumb: ['KAM-SFA', 'Orange', 'Lokalizator', 'Sprzedaż per urządzenie'],
    accentColor: ORANGE,
  },
  '/orange-agent-sales': {
    title: 'Orange — Sprzedaż per Agent',
    subtitle: 'Agregacja sprzedaży per agent (firma) z drill-down do salonów oraz danymi kontaktowymi RKSA / XDM / RDS D2D.',
    breadcrumb: ['KAM-SFA', 'Orange', 'Sprzedaż per Agent'],
    accentColor: ORANGE,
  },
  '/orange-daily-sales': {
    title: 'Orange — Sprzedaż dnia (R1)',
    subtitle: 'Sprzedaż wybranego dnia z podziałem OPOS / Lokalizator, agregacja per region / dystrykt / agent / KAM / salon.',
    breadcrumb: ['KAM-SFA', 'Orange', 'R1 · Sprzedaż dnia'],
    accentColor: ORANGE,
  },
  '/orange-shelf-sales': {
    title: 'Orange — Salony półkowe (R2)',
    subtitle: 'Sprzedaż salonów półkowych (Panel Bezpieczeństwa) — flagi 🚨 dla 3+ dni bez sprzedaży.',
    breadcrumb: ['KAM-SFA', 'Orange', 'R2 · Salony półkowe'],
    accentColor: ORANGE,
  },
  '/orange-lost-opportunities': {
    title: 'Orange — Utracone szanse (R3)',
    subtitle: 'Salony bez sprzedaży w bieżącym miesiącu z priorytetyzacją P1 / P2 / P3.',
    breadcrumb: ['KAM-SFA', 'Orange', 'R3 · Utracone szanse'],
    accentColor: ORANGE,
  },
  '/orange-region-summary': {
    title: 'Orange — Region / Dystrykt (podsumowanie)',
    subtitle: 'Rollup sprzedaży MTD per region i dystrykt — salony bez sprzedaży, śr. realizacja/POS, podział OPOS/Lokalizator.',
    breadcrumb: ['KAM-SFA', 'Orange', 'Region / Dystrykt'],
    accentColor: ORANGE,
  },

  // Plus
  '/plus-salon-analysis': {
    title: 'Plus — Analiza sprzedaży per salon',
    subtitle: 'Sprzedaż, pokrycie i wydajność per pojedynczy salon Plus.',
    breadcrumb: ['KAM-SFA', 'Plus', 'Analiza sprzedaży'],
    accentColor: PLUS,
  },
  '/plus-sales': {
    title: 'Plus — Raport sprzedaży [Grafana]',
    subtitle: 'Sprzedaż OnePlus / Plus Router — podgląd Grafany + agregacje.',
    breadcrumb: ['KAM-SFA', 'Plus', 'Raport sprzedaży'],
    accentColor: PLUS,
  },
  '/plus-vip-standard-zero': {
    title: 'Plus — VIP / Standard / Zero',
    subtitle: 'Klasyfikacja salonów Plus po wolumenie sprzedaży.',
    breadcrumb: ['KAM-SFA', 'Plus', 'VIP / Standard / Zero'],
    accentColor: PLUS,
  },
  '/plus-agent-sales': {
    title: 'Plus — Sprzedaż per Agent',
    subtitle: 'Agregacja sprzedaży per firma agencyjna (APS) z drill-down do salonów i kontaktów RKS.',
    breadcrumb: ['KAM-SFA', 'Plus', 'Sprzedaż per Agent'],
    accentColor: PLUS,
  },
  '/plus-region-rollup': {
    title: 'Plus — Region / RKS (podsumowanie)',
    subtitle: 'Rollup sprzedaży MTD per region i RKS, salony bez sprzedaży, śr. realizacja/POS, Watch Sigma / Opaska SOS + mapa salon↔KAM.',
    breadcrumb: ['KAM-SFA', 'Plus', 'Region / RKS'],
    accentColor: PLUS,
  },

  // T-Mobile
  '/tmobile-salon-analysis': {
    title: 'T-Mobile — Analiza sprzedaży per salon',
    subtitle: 'Sprzedaż, pokrycie i wydajność per salon T-Mobile.',
    breadcrumb: ['KAM-SFA', 'T-Mobile', 'Analiza sprzedaży'],
    accentColor: TMOBILE,
  },
  '/tmobile-agent-sales': {
    title: 'T-Mobile — Sprzedaż per Agent',
    subtitle: 'Lista agentów T-Mobile z drill-down do listy salonów + dane kontaktowe.',
    breadcrumb: ['KAM-SFA', 'T-Mobile', 'Sprzedaż per Agent'],
    accentColor: TMOBILE,
  },

  // Play
  '/play-salon-analysis': {
    title: 'Play — Analiza sprzedaży per salon',
    subtitle: 'Sprzedaż, pokrycie i wydajność per salon Play.',
    breadcrumb: ['KAM-SFA', 'Play', 'Analiza sprzedaży'],
    accentColor: PLAY,
  },
}

/**
 * <ReportPage> — wraps ReportShell, pulling page meta from PAGE_META.
 * Override any field via props: title, subtitle, breadcrumb, accentColor, actions, filterBar, kpiStrip.
 */
export default function ReportPage({ children, ...overrides }) {
  const location = useLocation()
  const meta = PAGE_META[location.pathname] || {}
  return (
    <ReportShell
      title={overrides.title ?? meta.title ?? 'KAM-SFA'}
      subtitle={overrides.subtitle ?? meta.subtitle}
      breadcrumb={overrides.breadcrumb ?? meta.breadcrumb}
      accentColor={overrides.accentColor ?? meta.accentColor}
      actions={overrides.actions}
      filterBar={overrides.filterBar}
      kpiStrip={overrides.kpiStrip}
      density={overrides.density}
    >
      {children}
    </ReportShell>
  )
}
